import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  emptyCampaignListStatusCounts,
  type CampaignDto,
  type CampaignListItem,
  type CampaignListSortRule,
  type CampaignListStatusCounts,
} from "@multi-chat/shared";
import { bulkDeleteCampaigns, listCampaigns } from "../../api/campaigns-api.js";
import {
  campaignListHref,
  campaignListItemFromDto,
  campaignVisibleInListQuery,
  insertCreatedCampaignInList,
  parseCampaignListViewQuery,
  type CampaignListViewQuery,
} from "./campaigns-presentation.js";

export function useCampaignList(organizationId: number): {
  items: CampaignListItem[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  status?: CampaignListViewQuery["status"];
  search: string;
  statusCounts: CampaignListStatusCounts;
  sort: CampaignListSortRule[];
  loading: boolean;
  error: unknown;
  selectedIds: Set<number>;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  setSearch: (search: string) => void;
  setSort: (sort: CampaignListSortRule[]) => void;
  toggleSelect: (campaignId: number) => void;
  selectIds: (campaignIds: readonly number[]) => void;
  clearSelection: () => void;
  reload: (options?: { silent?: boolean }) => Promise<void>;
  upsertCreated: (campaign: CampaignDto) => void;
  deleteSelected: (campaignIds: readonly number[]) => Promise<void>;
} {
  const location = useLocation();
  const navigate = useNavigate();
  const listSearch = location.pathname === "/campaigns" ? location.search : null;
  const [rememberedQuery, setRememberedQuery] = useState<CampaignListViewQuery>(() =>
    parseCampaignListViewQuery(location.pathname === "/campaigns" ? location.search : ""),
  );
  const query = useMemo(
    () => (listSearch != null ? parseCampaignListViewQuery(listSearch) : rememberedQuery),
    [listSearch, rememberedQuery],
  );
  const { page, pageSize, status, search, sort } = query;

  const [items, setItems] = useState<CampaignListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [statusCounts, setStatusCounts] = useState(emptyCampaignListStatusCounts);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (listSearch == null) return;
    setRememberedQuery(parseCampaignListViewQuery(listSearch));
  }, [listSearch]);

  const applyQuery = useCallback(
    (next: CampaignListViewQuery, options?: { replace?: boolean }) => {
      setRememberedQuery(next);
      setSelectedIds(new Set());
      if (location.pathname === "/campaigns") {
        navigate(campaignListHref(next), { replace: options?.replace !== false });
      }
    },
    [location.pathname, navigate],
  );

  const queryRef = useRef(query);
  queryRef.current = query;
  const applyQueryRef = useRef(applyQuery);
  applyQueryRef.current = applyQuery;

  const reload = useCallback(
    async (options?: { silent?: boolean }) => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      if (!options?.silent) setLoading(true);
      setError(null);
      try {
        const response = await listCampaigns(organizationId, {
          page,
          pageSize,
          status,
          search: search.length > 0 ? search : undefined,
          sort: sort != null && sort.length > 0 ? sort : undefined,
        });
        if (requestIdRef.current !== requestId) return;
        if (response.items.length === 0 && page > 1 && response.totalCount > 0) {
          applyQueryRef.current({
            ...queryRef.current,
            page: Math.max(1, response.totalPages),
          });
          return;
        }
        setItems(response.items);
        setTotalCount(response.totalCount);
        setTotalPages(response.totalPages);
        setStatusCounts(response.statusCounts);
      } catch (caught) {
        if (requestIdRef.current !== requestId) return;
        setError(caught);
        if (!options?.silent) {
          setItems([]);
          setTotalCount(0);
          setTotalPages(0);
        }
      } finally {
        if (requestIdRef.current === requestId && !options?.silent) {
          setLoading(false);
        }
      }
    },
    [organizationId, page, pageSize, search, sort, status],
  );

  const setPage = useCallback(
    (nextPage: number) => {
      applyQuery({ ...query, page: nextPage });
    },
    [applyQuery, query],
  );

  const setPageSize = useCallback(
    (nextPageSize: number) => {
      applyQuery({ ...query, page: 1, pageSize: nextPageSize });
    },
    [applyQuery, query],
  );

  const setSearch = useCallback(
    (nextSearch: string) => {
      applyQuery({ ...query, page: 1, search: nextSearch.trim() });
    },
    [applyQuery, query],
  );

  const setSort = useCallback(
    (nextSort: CampaignListSortRule[]) => {
      applyQuery({ ...query, page: 1, sort: nextSort });
    },
    [applyQuery, query],
  );

  const toggleSelect = useCallback((campaignId: number) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(campaignId)) next.delete(campaignId);
      else next.add(campaignId);
      return next;
    });
  }, []);

  const selectIds = useCallback((campaignIds: readonly number[]) => {
    setSelectedIds(new Set(campaignIds));
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const upsertCreated = useCallback(
    (campaign: CampaignDto) => {
      requestIdRef.current += 1;
      const created = campaignListItemFromDto(campaign);
      const visible = campaignVisibleInListQuery(created, query);
      setItems((current) => {
        const existed = current.some((item) => Number(item.id) === Number(created.id));
        if (!existed) {
          setStatusCounts((counts) => ({
            ...counts,
            all: counts.all + 1,
            DRAFT: counts.DRAFT + 1,
          }));
        }
        if (!visible) return current;
        if (!existed) {
          setTotalCount((count) => {
            const nextCount = count + 1;
            setTotalPages(Math.max(1, Math.ceil(nextCount / pageSize)));
            return nextCount;
          });
        }
        return insertCreatedCampaignInList(current, created, pageSize);
      });
      if (visible && query.page !== 1) {
        applyQuery({ ...query, page: 1 });
      }
      setLoading(false);
      setError(null);
    },
    [applyQuery, pageSize, query],
  );

  const deleteSelected = useCallback(
    async (campaignIds: readonly number[]) => {
      if (campaignIds.length === 0) return;
      await bulkDeleteCampaigns(organizationId, { campaignIds: [...campaignIds] });
      setSelectedIds(new Set());
      const removed = new Set(campaignIds.map(Number));
      setItems((current) => current.filter((item) => !removed.has(Number(item.id))));
      setTotalCount((count) => {
        const nextCount = Math.max(0, count - campaignIds.length);
        setTotalPages(nextCount === 0 ? 0 : Math.ceil(nextCount / pageSize));
        return nextCount;
      });
      await reload({ silent: true });
    },
    [organizationId, pageSize, reload],
  );

  useEffect(() => {
    void reload();
  }, [reload]);

  return {
    items,
    page,
    pageSize,
    totalCount,
    totalPages,
    status,
    search,
    statusCounts,
    sort: sort ?? [],
    loading,
    error,
    selectedIds,
    setPage,
    setPageSize,
    setSearch,
    setSort,
    toggleSelect,
    selectIds,
    clearSelection,
    reload,
    upsertCreated,
    deleteSelected,
  };
}
